/**
 * @fileOverview The employees walkthrough: the list, and one person's own page (Max Mustermann). Pure
 * data. See docs/view-help.md and docs/view-help/employees.md.
 */

import { ENTITY_ICONS } from "@/services/entityIcons"
import { FEATURE_SETS } from "@/services/featureSet"
import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/** Reading people — the list, a person's page and the Allgemein tab all need it. */
const GENERAL = [PERMISSIONS.VIEW_USER_GENERAL]

/**
 * The "Unternehmens-relevant" tab exists for anybody holding one of these, and its fields are drawn
 * for all of them (a field the reader may not see arrives masked, not missing). So its steps carry
 * the tab's own condition rather than a permission per field: they all appear together, and the first
 * of them is the step that clicks the tab.
 */
const COMPANY = [
  PERMISSIONS.VIEW_USER_COMPANY_GENERAL,
  PERMISSIONS.VIEW_USER_COMPANY_PAY,
  PERMISSIONS.VIEW_USER_COMPANY_TOTAL_HOLIDAY_DAYS,
  PERMISSIONS.VIEW_USER_COMPANY_SOCIAL_INSURANCE,
  PERMISSIONS.VIEW_USER_COMPANY_SOCIAL_INSURANCE_NUMBER,
]

/** The "System spezifisch" tab: the login address and the roles. Web users only. */
const SYSTEM = [PERMISSIONS.VIEW_USER_SYSTEM]

const FILES = [PERMISSIONS.VIEW_USER_FILE]
const EXTENDED = [PERMISSIONS.VIEW_USER_EXTENDED]

/**
 * Creating a user needs `EDIT_USER_GENERAL` **and** `EDIT_USER_SYSTEM` — the button is disabled
 * without both. A step gate is "at least one of", so the narrower of the two stands in for the pair.
 */
const CREATE = [PERMISSIONS.EDIT_USER_SYSTEM]

/** The working-time model block on the company tab: the add-on, and the right to read the models. */
const WORK_TIME_MODEL = [PERMISSIONS.VIEW_WORK_TIME_MODEL]

/** Reading the Personalrollen, for the related view that carries their hourly rates. */
const POSITIONS = [PERMISSIONS.VIEW_USER_POSITION_GENERAL]

/** Reading somebody else's recorded hours — the Zeiterfassung's Mitarbeiter view. */
const FOREIGN_TIME = [PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN]

/** The sample person every detail step is read on — the world's Max Mustermann, a login user. */
const MAX = { employeeId: "demo-1" }

/**
 * The list's rows, one anchor per person of the sample world (`WORLD_EMPLOYEES`): the region is the
 * rows together, so the dot sits on the first entry rather than on the table's search bar above it.
 * Only the rows on screen count towards the region, so a longer list would not make it taller.
 */
const ROWS = ["row-demo-1", "row-demo-2", "row-demo-3", "row-demo-4", "row-demo-5"]

const onOverview = stepsOn("EmployeeOverview", { permissions: GENERAL })
const onDetail = stepsOn("EmployeeDetail", { at: { params: MAX } })

// The related views: where an employee's properties come from. The pointer walks there (`services/viewHelp/ways.ts`);
// `at` says where to load the frame if that walk ever fails.
// The settings page and its Allgemein and Berechtigungen tabs carry no gate of their own — every
// signed-in user may open them (`router/routes/settings.js`, `settingsTabItems`).
const onSettings = stepsOn("SettingsOverview")
const onPositions = stepsOn("UserPositionOverview", { permissions: POSITIONS })
// And where a person's record is read again: the hours they record.
const onTeamTimes = stepsOn("TimeTrackingReview", {
  featureSets: [FEATURE_SETS.TIME_TRACKING],
  permissions: FOREIGN_TIME,
})

/**
 * Mitarbeiter: everybody the workspace keeps a record of, whether they sign in or not.
 *
 * The list first — its rows, the button a new person is created with, and the row that opens a
 * person; then Max Mustermann's own page: the kind of user he is, the Aktionen menu opened, and his
 * tabs one by one, each opened by a visible click on it, and last the save button. The create form
 * is not walked: the button's step says what it does. The Zusammenhänge chapter runs both ways: the
 * places an employee's properties are set up elsewhere — the roles in the settings, the default
 * employer's contribution beside them, and the Personalrollen with their hourly rates — and the hours
 * a person records, which are read against their record in the Zeiterfassung.
 *
 * The working-time model assignment on the company tab gets one step, which says it belongs to the
 * Zeiterfassung: what it does is the time-tracking walkthrough's own Zusammenhänge chapter.
 */
export const EMPLOYEES_TOUR: ViewHelpTour = {
  key: "employees",
  icon: ENTITY_ICONS.employee,
  routes: ["EmployeeOverview", "EmployeeDetail"],
  // Where an employee's properties come from: the roles that decide what a login user may do, the
  // workspace's default employer's contribution, and the Personalrollen an hour is priced by. Then
  // where the record is read again: the recorded hours.
  related: ["SettingsOverview", "UserPositionOverview", "TimeTrackingReview"],
  scenes: [
    // The Aktionen menu, opened to show what is in it and closed with Escape. Every item in it leaves
    // the page, deletes, converts or resets a password, so none is pressed.
    { key: "actions", enter: [{ do: "click", target: "employee-actions" }], exit: [{ do: "escape" }] },
    // Max's page, walked tab by tab: each step that opens a tab clicks it where the reader can see it,
    // and the steps on what is inside a tab follow it. One scene, so a jump into the middle clicks its
    // way there at speed first. Back on Allgemein only for a step of this page — going on to the
    // settings, the page is left as it is rather than switched back under the pointer as it walks away.
    {
      key: "tabs",
      enter: [],
      exit: [{ do: "openTab", target: "employee-tab-general" }],
      endsWithView: true,
    },
    // The settings' Allgemein tab, and back to Berechtigungen for a step of the same page.
    {
      key: "settings-tabs",
      enter: [],
      exit: [{ do: "openTab", target: "tab-rights" }],
      endsWithView: true,
    },
  ],
  steps: [
    // ── Übersicht ────────────────────────────────────────────────────────────
    onOverview("list", ROWS, ENTITY_ICONS.employee, { placement: "bottom" }),
    // Pointed at, never pressed: the window it opens asks which kind of user to create, and either
    // answer leaves the page for the create form.
    onOverview("create", "employee-create", "mdi-account-plus-outline", { placement: "left", permissions: CREATE }),
    // Last on the list, so the walk to Max's page that follows is the click this step talks about.
    onOverview("row", "employee-row-demo-1", "mdi-table-row", { placement: "bottom" }),

    // ── Eine Person ──────────────────────────────────────────────────────────
    onDetail("head", "employee-head", "mdi-card-account-details-outline", { placement: "bottom" }),
    onDetail("type", "employee-type", "mdi-account-key", { placement: "bottom" }),
    // Opened, never used: the scene presses the button and its Escape closes the menu again. The dot
    // sits on the button the reader will look for, not on the menu's first item; the camera still
    // shows the open list under it.
    onDetail("actions", "employee-actions", "mdi-account-cog-outline", { placement: "left", scene: "actions" }),
    // The menu's two ways out of the person, on the same open menu, pointed at and never pressed —
    // both leave the page. Gated like the entries, so a step never names one the reader lacks.
    onDetail("action-times", "employee-action-times", "mdi-clipboard-check-outline", {
      placement: "left",
      scene: "actions",
      featureSets: [FEATURE_SETS.TIME_TRACKING],
      permissions: FOREIGN_TIME,
    }),
    onDetail("action-planning", "employee-action-planning", ENTITY_ICONS.personnelplanning, {
      placement: "left",
      scene: "actions",
      featureSets: [FEATURE_SETS.PERSONNEL_PLANNING],
      permissions: [PERMISSIONS.VIEW_PERSONNELPLANNING],
    }),
    // The tab strip and the Allgemein tab under it, as one region: the strip says where everything
    // about the person is, and the open tab is the first example of it.
    onDetail("tabs", ["employee-tabs", "employee-general"], "mdi-tab", { placement: "right", permissions: GENERAL }),

    // The company tab, opened by the first of its steps. The Personalrolle is picked from the same
    // list the Zusammenhänge chapter ends on, so the pointer searches it and picks another one; the
    // picked value stays, because nothing after it depends on which role Max has.
    onDetail("company-position", "employee-position", "mdi-badge-account-horizontal-outline", {
      placement: "bottom",
      scene: "tabs",
      permissions: COMPANY,
      actions: [
        { do: "openTab", target: "employee-tab-company" },
        { do: "type", target: "employee-position", text: "Fach" },
        { do: "choose", text: "Facharbeiter" },
        { do: "escape" },
      ],
    }),
    onDetail("company-wage", ["employee-wage-type", "employee-wage-value"], "mdi-cash", {
      placement: "top",
      scene: "tabs",
      permissions: COMPANY,
    }),
    // One step, and it only says whose question this is: what the model does is the time-tracking
    // walkthrough's Zusammenhänge chapter, and explaining it twice would be two answers to keep in step.
    onDetail("company-worktime", "tt-assignment", "mdi-clock-outline", {
      placement: "top",
      scene: "tabs",
      featureSets: [FEATURE_SETS.TIME_TRACKING],
      permissions: WORK_TIME_MODEL,
    }),
    // The last rows of this tab sit against the card's sticky save footer. They need no scroll of
    // their own: `visibleView` counts what is pinned over the scroller's bottom edge as covered, so a
    // region is brought to rest above the footer rather than behind it.
    onDetail("company-contribution", "employee-contribution", "mdi-percent-outline", {
      placement: "top",
      scene: "tabs",
      permissions: COMPANY,
    }),
    onDetail("company-holidays", ["employee-holidays", "employee-insurance"], "mdi-beach", {
      placement: "top",
      scene: "tabs",
      permissions: COMPANY,
    }),

    // The system tab — only there for a login user, so only on a record that has one.
    onDetail("system-email", "employee-email", "mdi-email-outline", {
      placement: "bottom",
      scene: "tabs",
      permissions: SYSTEM,
      actions: [{ do: "openTab", target: "employee-tab-system" }],
    }),
    onDetail("system-roles", "employee-roles", "mdi-shield-account-outline", {
      placement: "top",
      scene: "tabs",
      permissions: SYSTEM,
    }),

    onDetail("files", "employee-files", "mdi-folder-outline", {
      placement: "top",
      scene: "tabs",
      permissions: FILES,
      actions: [{ do: "openTab", target: "employee-tab-files" }],
    }),
    onDetail("extended", "employee-extended", "mdi-tune-variant", {
      placement: "top",
      scene: "tabs",
      permissions: EXTENDED,
      actions: [{ do: "openTab", target: "employee-tab-extended" }],
    }),
    onDetail("activity", "employee-activity", "mdi-history", {
      placement: "top",
      scene: "tabs",
      permissions: GENERAL,
      actions: [{ do: "openTab", target: "employee-tab-activity" }],
    }),
    // Last of the page's own content, once every tab has been seen: the button is pinned under every
    // tab and saves all of them together. Explained, never pressed.
    onDetail("save", "employee-save", "mdi-content-save-outline", { placement: "top", scene: "tabs" }),

    // ── Where to find this again ─────────────────────────────────────────────
    onDetail("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // Other features' pages, so each step names the setting *and* where it takes effect on a
    // Mitarbeiter. Nothing about the forms themselves.
    //
    // First the three that feed a person's record.
    onSettings("rights", "settings-role-list", "mdi-shield-key-outline", {
      placement: "right",
      at: { hash: "rights" },
    }),
    onSettings("contribution", "settings-contribution", "mdi-percent-outline", {
      placement: "bottom",
      scene: "settings-tabs",
      at: { hash: "general" },
      actions: [{ do: "openTab", target: "tab-general" }],
    }),
    onPositions("positions", "row-pos-polier", ENTITY_ICONS.userposition, { placement: "bottom" }),

    // ── Wo der Datensatz wieder gelesen wird ─────────────────────────────────
    //
    // No zoom: the camera stays on the whole page, which is where "Tag für Tag" sits in relation to
    // the table it filters. Nothing is pressed — the region is always on screen, where it used to be
    // behind a tab this step opened.
    onTeamTimes("times", "tt-matrix", "mdi-view-grid-outline", {
      placement: "top",
      zoom: false,
    }),
  ],
}
